import React from 'react'
import { sendToNative, subscribeToNative } from '@mobi/web-native-comms/web'
import { logError } from '@core/Utils'
import { useAppDispatch } from '@core/Store/hooks'
import { setIsRadioBusy, setRadioPlaying } from '@core/Areas/Navigation/Store'
import { setCloseSkyVideoPlayer } from '@core/Areas/SkyVideoPlayer/Store'
import { RADIO_STREAM_URL } from '../constants'

export function useNativeRadio({
  isEnabled,
  isRadioPlaying,
}: {
  isEnabled: boolean
  isRadioPlaying: boolean
}) {
  const dispatch = useAppDispatch()

  // Synchronize Native Radio State Changes
  React.useEffect(() => {
    if (!isEnabled) return
    const { dispose } = subscribeToNative('NOTIFY_RADIO_STATE', data => {
      dispatch(setRadioPlaying(data.isPlaying))
      if (data.isPlaying) dispatch(setCloseSkyVideoPlayer())
      setIsRadioBusy(false)
    })
    return () => dispose()
  }, [dispatch, isEnabled])

  // Synchronize Radio state with UI on mount
  React.useEffect(() => {
    if (!isEnabled) return
    sendToNative('REQUEST_RADIO_STATE')
  }, [isEnabled])

  const pause = React.useCallback(() => {
    if (!isEnabled) return
    sendToNative('REQUEST_RADIO_STATE', { isPlaying: false })
    dispatch(setRadioPlaying(false))
  }, [dispatch, isEnabled])

  const play = React.useCallback(() => {
    if (!isEnabled) return

    if (!RADIO_STREAM_URL) {
      logError('Could not start radio. No streaming url configured', true)
      return
    }

    setIsRadioBusy(true)
    sendToNative('REQUEST_RADIO_STATE', { isPlaying: true, streamUrl: RADIO_STREAM_URL })
    dispatch(setCloseSkyVideoPlayer())
  }, [dispatch, isEnabled])

  const toggle = React.useCallback(() => {
    isRadioPlaying ? pause() : play()
  }, [pause, play, isRadioPlaying])

  return { toggle, pause }
}
